
import { useState } from 'react';
import { mockProducts } from '../../../mocks/products';

type Product = typeof mockProducts[0];

interface BulkEditModalProps {
  selectedCount: number;
  onApply: (changes: Partial<Omit<Product, 'id'>>) => void;
  onClose: () => void;
}

export default function BulkEditModal({ selectedCount, onApply, onClose }: BulkEditModalProps) {
  const [enabledFields, setEnabledFields] = useState<Record<string, boolean>>({
    category: false,
    pricing: false,
    commission: false,
    rating: false,
    tags: false,
  });

  const [category, setCategory] = useState('');
  const [pricing, setPricing] = useState('');
  const [commission, setCommission] = useState('');
  const [rating, setRating] = useState(4.5);
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagMode, setTagMode] = useState<'add' | 'replace'>('add');

  const toggleField = (field: string) => {
    setEnabledFields(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handleApply = () => {
    const changes: Partial<Omit<Product, 'id'>> = {};
    if (enabledFields.category && category.trim()) changes.category = category.trim();
    if (enabledFields.pricing && pricing.trim()) changes.pricing = pricing.trim();
    if (enabledFields.commission && commission.trim()) changes.commission = commission.trim();
    if (enabledFields.rating) changes.rating = rating;
    if (enabledFields.tags) {
      (changes as any)._tagMode = tagMode;
      (changes as any)._tags = tags;
    }
    onApply(changes);
  };

  const hasAnyEnabled = Object.values(enabledFields).some(Boolean);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Bulk Edit Products</h2>
            <p className="text-sm text-gray-500 mt-0.5">{selectedCount} product{selectedCount !== 1 ? 's' : ''} selected</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          <p className="text-sm text-gray-600 bg-amber-50 border border-amber-200 px-4 py-3 rounded-md">
            <i className="ri-information-line text-amber-500 mr-1.5"></i>
            Toggle the fields you want to update. Only enabled fields will be changed across all selected products.
          </p>

          {/* Category */}
          <div className={`border rounded-lg p-4 transition-colors ${enabledFields.category ? 'border-teal-300 bg-teal-50/30' : 'border-gray-200 bg-gray-50/50'}`}>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-bold text-gray-700">Category</label>
              <button
                type="button"
                onClick={() => toggleField('category')}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${enabledFields.category ? 'bg-teal-500' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabledFields.category ? 'left-5.5 translate-x-0.5' : 'left-0.5'}`}></span>
              </button>
            </div>
            {enabledFields.category && (
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 text-sm text-gray-900 focus:outline-none focus:border-teal-500 rounded-md"
                placeholder="e.g., Marketing Tools, Web Hosting"
              />
            )}
          </div>

          {/* Pricing */}
          <div className={`border rounded-lg p-4 transition-colors ${enabledFields.pricing ? 'border-teal-300 bg-teal-50/30' : 'border-gray-200 bg-gray-50/50'}`}>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-bold text-gray-700">Pricing</label>
              <button
                type="button"
                onClick={() => toggleField('pricing')}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${enabledFields.pricing ? 'bg-teal-500' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabledFields.pricing ? 'left-5.5 translate-x-0.5' : 'left-0.5'}`}></span>
              </button>
            </div>
            {enabledFields.pricing && (
              <input
                type="text"
                value={pricing}
                onChange={(e) => setPricing(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 text-sm text-gray-900 focus:outline-none focus:border-teal-500 rounded-md"
                placeholder="e.g., $29/mo, Free, $99 one-time"
              />
            )}
          </div>

          {/* Commission */}
          <div className={`border rounded-lg p-4 transition-colors ${enabledFields.commission ? 'border-teal-300 bg-teal-50/30' : 'border-gray-200 bg-gray-50/50'}`}>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-bold text-gray-700">Commission Rate</label>
              <button
                type="button"
                onClick={() => toggleField('commission')}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${enabledFields.commission ? 'bg-teal-500' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabledFields.commission ? 'left-5.5 translate-x-0.5' : 'left-0.5'}`}></span>
              </button>
            </div>
            {enabledFields.commission && (
              <input
                type="text"
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 text-sm text-gray-900 focus:outline-none focus:border-teal-500 rounded-md"
                placeholder="e.g., 30%, $50 per sale"
              />
            )}
          </div>

          {/* Rating */}
          <div className={`border rounded-lg p-4 transition-colors ${enabledFields.rating ? 'border-teal-300 bg-teal-50/30' : 'border-gray-200 bg-gray-50/50'}`}>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-bold text-gray-700">Rating</label>
              <button
                type="button"
                onClick={() => toggleField('rating')}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${enabledFields.rating ? 'bg-teal-500' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabledFields.rating ? 'left-5.5 translate-x-0.5' : 'left-0.5'}`}></span>
              </button>
            </div>
            {enabledFields.rating && (
              <input
                type="number"
                min="0"
                max="5"
                step="0.1"
                value={rating}
                onChange={(e) => setRating(parseFloat(e.target.value))}
                className="w-full px-4 py-2.5 border border-gray-300 text-sm text-gray-900 focus:outline-none focus:border-teal-500 rounded-md"
              />
            )}
          </div>

          {/* Tags */}
          <div className={`border rounded-lg p-4 transition-colors ${enabledFields.tags ? 'border-teal-300 bg-teal-50/30' : 'border-gray-200 bg-gray-50/50'}`}>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-bold text-gray-700">Tags</label>
              <button
                type="button"
                onClick={() => toggleField('tags')}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${enabledFields.tags ? 'bg-teal-500' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabledFields.tags ? 'left-5.5 translate-x-0.5' : 'left-0.5'}`}></span>
              </button>
            </div>
            {enabledFields.tags && (
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <button
                    type="button"
                    onClick={() => setTagMode('add')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md cursor-pointer transition-colors whitespace-nowrap ${tagMode === 'add' ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                  >
                    Add to existing
                  </button>
                  <button
                    type="button"
                    onClick={() => setTagMode('replace')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md cursor-pointer transition-colors whitespace-nowrap ${tagMode === 'replace' ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                  >
                    Replace all tags
                  </button>
                </div>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    className="flex-1 px-4 py-2.5 border border-gray-300 text-sm text-gray-900 focus:outline-none focus:border-teal-500 rounded-md"
                    placeholder="Type a tag and press Enter"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap rounded-md"
                  >
                    Add
                  </button>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-md"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="w-4 h-4 flex items-center justify-center text-teal-500 hover:text-teal-700 cursor-pointer"
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap rounded-md"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!hasAnyEnabled}
            className={`px-6 py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap rounded-md ${hasAnyEnabled ? 'bg-teal-500 text-white hover:bg-teal-600' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
          >
            Apply to {selectedCount} Product{selectedCount !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
